import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  CheckCircle, 
  TrendingUp,
  Bell,
  BarChart3,
  Settings,
  FileText,
  Target,
  Zap,
  MessageSquare,
  Video,
  Activity,
  Calendar,
  Clock,
  ExternalLink,
  HelpCircle,
  BookOpen,
  CheckSquare,
  DollarSign,
  Hash,
  Plus
} from 'lucide-react';
import { 
  OnboardingProject, 
  OnboardingTask, 
  OnboardingMetrics, 
  AgenticReminder,
  OnboardingHubState
} from './types';
import { 
  mockProjects, 
  mockTasks, 
  mockMetrics, 
  mockReminders
} from './mockData';
import CommunicationHub from '../CommunicationHub/CommunicationHub';
import { ClientCommHub } from '../CommunicationHub/ClientCommHub';
import { MeetingScribePage } from '../MeetingScribe/MeetingScribePage';
import ClientView from './ClientView';
import { ConversionMetricsDashboard } from './ConversionMetricsDashboard';
import { UnifiedResourceManagement } from './UnifiedResourceManagement';
import { TimelineView } from './TimelineView';
import { DemoWorkspace } from './DemoWorkspace';
import { DemoWorkspaceView } from './DemoWorkspaceView';
import { ProjectPicker } from './ProjectPicker';
import { MessagesView } from './MessagesView';
import { KnowledgeView } from './KnowledgeView';
import { ConversionMetricsView } from './ConversionMetricsView';
import { MagicLinkIntegration } from './MagicLinkIntegration';
import { SimpleTimeTracking } from './SimpleTimeTracking';
import { SimpleBoardView } from './SimpleBoardView';
import { ClientKanbanBoard } from './ClientKanbanBoard';
import { SimpleNotifications } from './SimpleNotifications';
import { SimpleEmailWebhook } from './SimpleEmailWebhook';
import { EmbedModeWrapper } from './EmbedModeWrapper';
import { ProgramDashboard } from './ProgramDashboard';
import { SandboxMode } from './SandboxMode';
import { AutoAssignmentEngine } from './AutoAssignmentEngine';
import { PlanOptimizer } from './PlanOptimizer';
import { DragDropDashboard } from './DragDropDashboard';
import { HiveMindInsights } from './HiveMindInsights';
import { BillingIntegration } from './BillingIntegration';
import { NotificationSystem } from './NotificationSystem';
import { TimeTrackingView } from './TimeTrackingView';
import { WorkObjectsPanel } from '../WorkObjectsPanel';
import PMSenseiPanel from './PMSenseiPanel';

import LaunchPadSettings from './LaunchPadSettings';
import FilesPanel from './FilesPanel';
import BillingManagement from './BillingManagement';

import { MAPPanel } from './MAPPanel';
import { TimesheetPanel } from './TimesheetPanel';
import { IntakeFormPanel } from './IntakeFormPanel';

const OnboardingHub: React.FC = () => {
  const [state, setState] = useState({
    activeView: 'dashboard',
    filters: {
      status: [],
      priority: [],
      assignee: [],
      projectType: []
    },
    searchQuery: '',
    sortBy: 'dueDate',
    sortOrder: 'asc'
  });
  
  const [communicationView, setCommunicationView] = useState<'chat' | 'meetings' | 'activity'>('chat');

  const [viewMode, setViewMode] = useState<'overview' | 'work' | 'timeline' | 'charts' | 'resources' | 'timetracking' | 'billing' | 'files' | 'notifications' | 'messages' | 'support' | 'knowledge' | 'demo' | 'admin' | 'program' | 'sandbox' | 'maps' | 'timesheets' | 'intake'>('overview');
  const [userRole, setUserRole] = useState<'client' | 'vendor' | 'admin'>('client');
  const [isDetached, setIsDetached] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  const [projects] = useState<OnboardingProject[]>(mockProjects);
  const [tasks] = useState<OnboardingTask[]>(mockTasks);
  const [metrics] = useState<OnboardingMetrics>(mockMetrics);
  const [reminders] = useState<AgenticReminder[]>(mockReminders);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setIsDetached(urlParams.get('detached') === '1');
  }, []);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-400';
      case 'active': case 'in-progress': return 'text-slate-400';
      case 'at-risk': return 'text-yellow-400';
      case 'overdue': case 'critical': return 'text-red-400';
      case 'planning': case 'pending': return 'text-[#5A7BC4]';
      default: return 'text-gray-400';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-400 bg-red-400/10';
      case 'high': return 'text-orange-400 bg-orange-400/10';
      case 'medium': return 'text-yellow-400 bg-yellow-400/10';
      case 'low': return 'text-green-400 bg-green-400/10';
      default: return 'text-gray-400 bg-white';
    }
  };

  const renderDashboard = () => (
    <div className="space-y-8">
      {/* Premium Tyler Technologies Header */}
      <div className="p-8 mb-8 rounded-xl bg-white border border-gray-200 shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-6 lg:space-y-0">
          <div className="flex items-center space-x-6">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-white to-gray-50 flex items-center justify-center shadow-xl shadow-[#4267A1]/25 relative overflow-hidden border border-gray-200">
              <img 
                src="/tyler-technologies-logo.png" 
                alt="Tyler Technologies Logo" 
                className="w-16 h-16 object-contain drop-shadow-sm"
                onError={(e) => console.error('Tyler Technologies logo failed to load:', e)}
              />
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-[#4267A1] via-[#5A7BC4] to-[#4267A1] bg-clip-text text-transparent mb-2 drop-shadow-sm">
                Success Bridge
              </h1>
              <p className="text-lg text-gray-600 font-medium">
                Agentic project onboarding and delivery management
              </p>
            </div>
          </div>
          
          {/* Enhanced Five-Tab Navigation */}
          <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-1 bg-white rounded-2xl p-1.5 border border-gray-200 shadow-lg shadow-gray-200/50">
              {[
                { key: 'overview', label: 'Overview', icon: Users, color: 'text-[#4267A1]' },
                { key: 'work', label: 'Work', icon: CheckCircle, color: 'text-[#4267A1]' },
                { key: 'timeline', label: 'Timeline', icon: Calendar, color: 'text-orange-600' },
                { key: 'charts', label: 'Charts', icon: BarChart3, color: 'text-[#8A9150]' },
                { key: 'resources', label: 'Resources', icon: Users, color: 'text-purple-600' },
                { key: 'timetracking', label: 'Time', icon: Clock, color: 'text-blue-600' },
                { key: 'billing', label: 'Billing', icon: DollarSign, color: 'text-green-600' },
                { key: 'notifications', label: 'Alerts', icon: Bell, color: 'text-yellow-600' },
                { key: 'messages', label: 'Messages', icon: MessageSquare, color: 'text-blue-600' },
                { key: 'support', label: 'Support', icon: HelpCircle, color: 'text-purple-600' },
                { key: 'knowledge', label: 'Knowledge', icon: BookOpen, color: 'text-green-600' },
                { key: 'demo', label: 'Demo', icon: Activity, color: 'text-pink-600' },
                { key: 'maps', label: 'MAPs', icon: Target, color: 'text-purple-600' },
                { key: 'timesheets', label: 'Timesheets', icon: Clock, color: 'text-orange-600' },
                { key: 'intake', label: 'Intake', icon: FileText, color: 'text-indigo-600' },
                ...(userRole === 'admin' ? [
                  { key: 'admin', label: 'Admin', icon: Settings, color: 'text-gray-600' },
                  { key: 'program', label: 'Program', icon: BarChart3, color: 'text-indigo-600' },
                  { key: 'sandbox', label: 'Sandbox', icon: Target, color: 'text-teal-600' }
                ] : [])
              ].map(({ key, label, icon: Icon, color }) => (
                <button
                  key={key}
                  onClick={() => setViewMode(key as any)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all duration-300 relative overflow-hidden ${
                    viewMode === key 
                      ? 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white shadow-lg shadow-[#4267A1]/30 transform scale-105' 
                      : `text-gray-600 hover:text-gray-900 hover:bg-white hover:shadow-md ${color}`
                  }`}
                >
                  {viewMode === key && (
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"></div>
                  )}
                  <Icon className="w-4 h-4 relative z-10" />
                  <span className="hidden sm:inline font-medium relative z-10">{label}</span>
                </button>
              ))}
            </div>
            <button 
              onClick={() => {
                const currentUrl = window.location.href;
                const detachedUrl = currentUrl.includes('?') 
                  ? `${currentUrl}&detached=1` 
                  : `${currentUrl}?detached=1`;
                
                const newWindow = window.open(
                  detachedUrl,
                  'onboarding-hub',
                  'width=1400,height=900,scrollbars=yes,resizable=yes'
                );

                if (newWindow) {
                  newWindow.addEventListener('beforeunload', () => {
                    localStorage.setItem('onboarding-hub-window', JSON.stringify({
                      width: newWindow.outerWidth,
                      height: newWindow.outerHeight,
                      x: newWindow.screenX,
                      y: newWindow.screenY
                    }));
                  });
                }
              }}
              className="flex items-center justify-center w-12 h-12 text-sm font-semibold bg-gradient-to-r from-[#8A9150] via-[#A5B366] to-[#B8C77A] text-white rounded-xl hover:shadow-xl hover:shadow-[#8A9150]/40 transition-all duration-300 transform hover:scale-105 relative overflow-hidden"
              title="Pop out to new window"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"></div>
              <ExternalLink className="w-5 h-5 relative z-10" />
            </button>
          </div>
          
          <div className="text-left lg:text-right">
            <p className="tyler-body-sm text-gray-500 mb-2">Today</p>
            <p className="tyler-heading-md text-gray-900">
              {currentTime.toLocaleDateString('en-US', { 
                weekday: 'long',
                month: 'short',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Premium Key Metrics Cards with Enhanced Modern Design */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="tyler-card p-10 group relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#4267A1]/5 to-[#5A7BC4]/5 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-8">
              <div className="p-5 bg-gradient-to-br from-[#4267A1]/8 to-[#5A7BC4]/8 rounded-3xl group-hover:shadow-xl group-hover:shadow-[#4267A1]/15 transition-all duration-500 group-hover:scale-110">
                <Target className="w-10 h-10 text-[#4267A1]" />
              </div>
              <div className="text-right">
                <p className="tyler-body-sm text-gray-500 mb-2 font-medium">Active Projects</p>
                <p className="text-5xl font-black text-[#4267A1] group-hover:scale-105 transition-transform duration-500">{metrics.activeProjects}</p>
              </div>
            </div>
            <div className="tyler-progress-bar mb-4">
              <motion.div
                className="tyler-progress-fill"
                initial={{ width: 0 }}
                animate={{ width: "85%" }}
                transition={{ duration: 2, ease: "easeOut" }}
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="tyler-body-sm text-gray-600 font-medium">Progress</span>
              <span className="tyler-body-sm text-[#4267A1] font-bold">85%</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          className="tyler-card p-10 group relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#8A9150]/5 to-[#A5B366]/5 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-8">
              <div className="p-5 bg-gradient-to-br from-[#8A9150]/8 to-[#A5B366]/8 rounded-3xl group-hover:shadow-xl group-hover:shadow-[#8A9150]/15 transition-all duration-500 group-hover:scale-110">
                <CheckCircle className="w-10 h-10 text-[#8A9150]" />
              </div>
              <div className="text-right">
                <p className="tyler-body-sm text-gray-500 mb-2 font-medium">On-Time Delivery</p>
                <p className="text-5xl font-black text-[#8A9150] group-hover:scale-105 transition-transform duration-500">{metrics.onTimeDeliveryRate}%</p>
              </div>
            </div>
            <div className="tyler-progress-bar mb-4">
              <motion.div
                className="h-full bg-gradient-to-r from-[#8A9150] to-[#A5B366] rounded-full tyler-progress-fill"
                initial={{ width: 0 }}
                animate={{ width: `${metrics.onTimeDeliveryRate}%` }}
                transition={{ duration: 2, ease: "easeOut" }}
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="tyler-body-sm text-gray-600 font-medium">Target: 95%</span>
              <span className="tyler-body-sm text-[#8A9150] font-bold">{metrics.onTimeDeliveryRate}%</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          className="tyler-card p-10 group relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/5 to-indigo-500/5 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-8">
              <div className="p-5 bg-gradient-to-br from-purple-500/8 to-indigo-500/8 rounded-3xl group-hover:shadow-xl group-hover:shadow-purple-500/15 transition-all duration-500 group-hover:scale-110">
                <Users className="w-10 h-10 text-purple-600" />
              </div>
              <div className="text-right">
                <p className="tyler-body-sm text-gray-500 mb-2 font-medium">Team Utilization</p>
                <p className="text-5xl font-black text-purple-600 group-hover:scale-105 transition-transform duration-500">{metrics.teamUtilization}%</p>
              </div>
            </div>
            <div className="tyler-progress-bar mb-4">
              <motion.div
                className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full tyler-progress-fill"
                initial={{ width: 0 }}
                animate={{ width: `${metrics.teamUtilization}%` }}
                transition={{ duration: 2, ease: "easeOut" }}
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="tyler-body-sm text-gray-600 font-medium">Optimal: 80%</span>
              <span className="tyler-body-sm text-purple-600 font-bold">{metrics.teamUtilization}%</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          className="tyler-card p-10 group relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/5 to-yellow-500/5 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-8">
              <div className="p-5 bg-gradient-to-br from-orange-500/8 to-yellow-500/8 rounded-3xl group-hover:shadow-xl group-hover:shadow-orange-500/15 transition-all duration-500 group-hover:scale-110">
                <TrendingUp className="w-10 h-10 text-orange-600" />
              </div>
              <div className="text-right">
                <p className="tyler-body-sm text-gray-500 mb-2 font-medium">Client Satisfaction</p>
                <p className="text-5xl font-black text-orange-600 group-hover:scale-105 transition-transform duration-500">{metrics.clientSatisfactionScore}/5</p>
              </div>
            </div>
            <div className="tyler-progress-bar mb-4">
              <motion.div
                className="h-full bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full tyler-progress-fill"
                initial={{ width: 0 }}
                animate={{ width: "90%" }}
                transition={{ duration: 2, ease: "easeOut" }}
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="tyler-body-sm text-gray-600 font-medium">Target: 4.5</span>
              <span className="tyler-body-sm text-orange-600 font-bold">{metrics.clientSatisfactionScore}/5</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Agentic Reminders & Guidance */}
      {reminders.length > 0 && (
        <div className="geex-card backdrop-blur-md border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-yellow-400" />
            <h3 className="text-lg font-semibold bg-gradient-to-r from-[#F6E7C1] to-[#E7CBA1] bg-clip-text text-transparent">PMSENSAI Guidance</h3>
          </div>
          <div className="space-y-3">
            {reminders.slice(0, 3).map((reminder) => (
              <motion.div
                key={reminder.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`p-4 rounded-lg border-l-4 ${
                  reminder.priority === 'critical' ? 'border-red-400 bg-red-400/10' :
                  reminder.priority === 'high' ? 'border-orange-400 bg-orange-400/10' :
                  'border-yellow-400 bg-yellow-400/10'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-white">{reminder.title}</h4>
                    <p className="text-sm text-slate-300 mt-1">{reminder.message}</p>
                    {reminder.suggestedActions.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-400 mb-1">Suggested Actions:</p>
                        <ul className="text-xs text-slate-300 space-y-1">
                          {reminder.suggestedActions.slice(0, 2).map((action, idx) => (
                            <li key={idx} className="flex items-center gap-1">
                              <div className="w-1 h-1 bg-gray-400 rounded-full" />
                              {action}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <button className="text-xs text-purple-400 hover:text-[#7bb8ff] ml-4">
                    Take Action
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Active Projects Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="geex-card backdrop-blur-md border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold bg-gradient-to-r from-[#F6E7C1] to-[#E7CBA1] bg-clip-text text-transparent">Active Projects</h3>
            <button 
              onClick={() => setState(prev => ({ ...prev, activeView: 'projects' }))}
              className="text-sm text-purple-400 hover:text-[#7bb8ff]"
            >
              View All
            </button>
          </div>
          <div className="space-y-3">
            {projects.filter(p => p.status === 'active').slice(0, 3).map((project) => (
              <motion.div
                key={project.id}
                whileHover={{ scale: 1.01 }}
                className="p-4 geex-card rounded-lg border border-slate-700/30 cursor-pointer"
                onClick={() => setState(prev => ({ ...prev, selectedProject: project, activeView: 'projects' }))}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-white">{project.name}</h4>
                  <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(project.priority)}`}>
                    {project.priority}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-400">
                  <span>{project.clientName}</span>
                  <span>•</span>
                  <span>{project.progress}% complete</span>
                </div>
                <div className="mt-2 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${project.progress}%` }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="geex-card backdrop-blur-md border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold bg-gradient-to-r from-[#F6E7C1] to-[#E7CBA1] bg-clip-text text-transparent">Upcoming Tasks</h3>
            <button 
              onClick={() => setState(prev => ({ ...prev, activeView: 'tasks' }))}
              className="text-sm text-purple-400 hover:text-[#7bb8ff]"
            >
              View All
            </button>
          </div>
          <div className="space-y-3">
            {tasks.filter(t => t.status !== 'completed').slice(0, 4).map((task) => (
              <motion.div
                key={task.id}
                whileHover={{ scale: 1.01 }}
                className="p-3 geex-card rounded-lg border border-slate-700/30 cursor-pointer"
                onClick={() => setState(prev => ({ ...prev, selectedTask: task, activeView: 'tasks' }))}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-white">{task.title}</h4>
                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                      <span>{task.assignee}</span>
                      <span>•</span>
                      <span>Due {new Date(task.dueDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <span className={`text-xs ${getStatusColor(task.status)}`}>
                    {task.status}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderNavigation = () => (
    <div className="flex items-center gap-1 mb-6 geex-card backdrop-blur-md border border-slate-700/50 rounded-xl p-1">
      {[
        { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
        { key: 'projects', label: 'Projects', icon: Target },
        { key: 'tasks', label: 'Tasks', icon: CheckCircle },
        { key: 'communication', label: 'Communication', icon: MessageSquare },
        { key: 'agentic', label: 'AI Insights', icon: Zap },
        { key: 'templates', label: 'Templates', icon: FileText },
        { key: 'analytics', label: 'Analytics', icon: TrendingUp }
      ].map(({ key, label, icon: Icon }) => (
        <button
          key={key}
          onClick={() => setState(prev => ({ ...prev, activeView: key as any }))}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            state.activeView === key
              ? 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white border border-[#5A7BC4]/50'
              : 'text-gray-600 hover:text-gray-900 hover:bg-white'
          }`}
        >
          <Icon className="w-4 h-4" />
          {label}
        </button>
      ))}
    </div>
  );


  const renderCommunication = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-[#F6E7C1] to-[#E7CBA1] bg-clip-text text-transparent mb-2">Communication Hub</h2>
          <p className="text-slate-300">
            Integrated chat, meetings, and project activity feed
          </p>
        </div>
        
        {/* Communication Sub-Navigation */}
        <div className="flex items-center space-x-1 geex-card backdrop-blur-md rounded-xl p-1">
          {[
            { id: 'chat', label: 'Team Chat', icon: MessageSquare },
            { id: 'meetings', label: 'Meetings', icon: Video },
            { id: 'activity', label: 'Activity Feed', icon: Activity }
          ].map((tab) => {
            const IconComponent = tab.icon;
            const isActive = communicationView === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setCommunicationView(tab.id as any)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                  isActive 
                    ? 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white shadow-lg' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white'
                }`}
              >
                <IconComponent className="w-4 h-4" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Communication Content */}
      <div className="h-[600px]">
        {communicationView === 'chat' && (
          <ClientCommHub 
            projectId={mockProjects[0].id}
            userRole="client"
            className="h-full"
          />
        )}
        
        {communicationView === 'meetings' && (
          <MeetingScribePage />
        )}
        
        {communicationView === 'activity' && (
          <div className="h-full geex-card backdrop-blur-md border border-slate-700/50 rounded-xl p-6">
            <div className="space-y-4">
              <h4 className="text-lg font-semibold bg-gradient-to-r from-[#F6E7C1] to-[#E7CBA1] bg-clip-text text-transparent mb-4">Live Project Activity Feed</h4>
              
              {/* Activity Items */}
              <div className="space-y-3">
                {[
                  {
                    id: 1,
                    type: 'task_completed',
                    user: 'Sarah Chen',
                    action: 'completed task',
                    target: 'Database Migration Plan',
                    project: 'Digital Transformation',
                    timestamp: '2 minutes ago',
                    icon: CheckCircle,
                    color: 'text-green-400'
                  },
                  {
                    id: 2,
                    type: 'meeting_scheduled',
                    user: 'ProjectBot',
                    action: 'scheduled meeting',
                    target: 'Weekly Sync',
                    project: 'Customer Portal Redesign',
                    timestamp: '15 minutes ago',
                    icon: Calendar,
                    color: 'text-slate-400'
                  },
                  {
                    id: 3,
                    type: 'risk_identified',
                    user: 'PMSENSAI',
                    action: 'identified risk',
                    target: 'Resource Constraints',
                    project: 'API Integration',
                    timestamp: '1 hour ago',
                    icon: Bell,
                    color: 'text-orange-400'
                  },
                  {
                    id: 4,
                    type: 'file_uploaded',
                    user: 'Alex Rodriguez',
                    action: 'uploaded file',
                    target: 'Technical Specifications.pdf',
                    project: 'Digital Transformation',
                    timestamp: '2 hours ago',
                    icon: FileText,
                    color: 'text-[#5A7BC4]'
                  }
                ].map((activity) => {
                  const IconComponent = activity.icon;
                  
                  return (
                    <motion.div
                      key={activity.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-start space-x-3 p-4 tyler-card rounded-lg border border-gray-200 hover:bg-white transition-colors"
                    >
                      <div className={`p-2 rounded-lg bg-white border border-gray-200 ${activity.color}`}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-white">
                            <span className="font-medium">{activity.user}</span>
                            <span className="text-slate-400 mx-1">{activity.action}</span>
                            <span className="font-medium text-[#7bb8ff]">{activity.target}</span>
                          </p>
                          <span className="text-xs text-slate-500">{activity.timestamp}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">in {activity.project}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              
              {/* Health Status Summary */}
              <div className="mt-6 p-4 bg-gradient-to-r from-green-500/10 to-slate-500/10 rounded-xl border border-green-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-green-300">Overall Project Health</h5>
                    <p className="text-sm text-slate-400 mt-1">All projects are on track with no critical issues</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-green-300">Healthy</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    const currentProject = projects[0];
    const currentTasks = tasks.filter(t => t.projectId === currentProject?.id);

    switch (viewMode) {
      case 'overview':
        return (
          <div className="h-full">
            <ClientView 
              project={currentProject} 
              tasks={currentTasks}
              onTaskUpdate={(taskId, status) => {
                console.log('Task updated:', taskId, status);
              }}
            />
          </div>
        );

      case 'work':
        return (
          <div className="h-full">
            <div className="h-full bg-white">
              <div className="p-8">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4">
                    Work Objects
                  </h2>
                  <p className="text-gray-600 text-lg">
                    Track and manage your project tasks and deliverables
                  </p>
                </div>
                <div className="rounded-xl overflow-hidden bg-white border border-gray-200 shadow-lg">
                  <WorkObjectsPanel />
                </div>
              </div>
            </div>
          </div>
        );

      case 'timeline':
        return (
          <div className="h-full">
            <TimelineView 
              project={currentProject}
              tasks={currentTasks}
              onTaskUpdate={(taskId, updates) => {
                console.log('Task updated:', taskId, updates);
              }}
            />
          </div>
        );

      case 'charts':
        return (
          <div className="h-full">
            {userRole === 'admin' ? <DragDropDashboard /> : <ConversionMetricsView />}
          </div>
        );

      case 'resources':
        return (
          <div className="h-full">
            <UnifiedResourceManagement 
              project={currentProject} 
              tasks={currentTasks}
              onResourceReallocation={(taskId, newAssignee) => console.log('Reallocate:', taskId, newAssignee)}
              onCapacityAdjustment={(userId, newCapacity) => console.log('Adjust capacity:', userId, newCapacity)}
            />
          </div>
        );

      case 'timetracking':
        return (
          <div className="h-full">
            <TimeTrackingView projectId="proj-001" userRole={userRole} />
          </div>
        );

      case 'billing':
        return (
          <div className="h-full">
            <BillingManagement projectId="proj-001" userRole={userRole} />
          </div>
        );

      case 'files':
        return (
          <div className="h-full">
            <FilesPanel projectId={currentProject?.id || 'proj-001'} userRole={userRole} />
          </div>
        );

      case 'notifications':
        return (
          <div className="h-full">
            <NotificationSystem />
          </div>
        );

      case 'messages':
        return (
          <div className="h-full">
            <MessagesView />
          </div>
        );

      case 'knowledge':
        return (
          <div className="h-full">
            <KnowledgeView />
          </div>
        );

      case 'demo':
        return (
          <div className="h-full">
            <DemoWorkspaceView projectId="proj-001" />
          </div>
        );

      case 'admin':
        return (
          <div className="h-full bg-white">
            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4">
                  Admin Features
                </h2>
                <p className="text-gray-600 text-lg">
                  Advanced onboarding management and integration tools
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <MagicLinkIntegration projectId="proj-001" userRole={userRole} />
                  <SimpleTimeTracking projectId="proj-001" userRole={userRole} />
                  <SimpleBoardView projectId="proj-001" userRole={userRole} />
                  <AutoAssignmentEngine projectId="proj-001" userRole={userRole} />
                </div>
                <div className="space-y-6">
                  <SimpleNotifications projectId="proj-001" userRole={userRole} />
                  <SimpleEmailWebhook projectId="proj-001" userRole={userRole} />
                  <PlanOptimizer projectId="proj-001" userRole={userRole} />
                  <HiveMindInsights />
                  <BillingIntegration projectId="proj-001" userRole={userRole} />
                </div>
              </div>
            </div>
          </div>
        );

      case 'program':
        return (
          <div className="h-full">
            <ProgramDashboard />
          </div>
        );

      case 'sandbox':
        return (
          <div className="h-full bg-white">
            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4">
                  Sandbox Environment
                </h2>
                <p className="text-gray-600 text-lg">
                  Safe testing environment for Tyler Technologies workflows
                </p>
              </div>
              <SandboxMode projectId="proj-001" userRole={userRole} />
            </div>
          </div>
        );

      case 'maps':
        return (
          <div className="h-full">
            <MAPPanel 
              projectId={projects[0]?.id || 'proj-001'} 
              userRole={userRole}
              onConvertToOnboarding={(mapId) => {
                console.log('Converting MAP to onboarding plan:', mapId);
                // TODO: Implement conversion logic
              }}
            />
          </div>
        );

      case 'timesheets':
        return (
          <div className="h-full">
            <TimesheetPanel 
              projectId={projects[0]?.id || 'proj-001'} 
              userRole={userRole}
              onExportToBilling={(entries) => {
                console.log('Exporting to billing:', entries);
                // TODO: Implement billing export
              }}
            />
          </div>
        );

      case 'intake':
        return (
          <div className="h-full">
            <IntakeFormPanel 
              projectId={projects[0]?.id || 'proj-001'} 
              userRole={userRole}
              onFormSubmit={(submission) => {
                console.log('Form submitted:', submission);
                // TODO: Implement form submission handling
              }}
            />
          </div>
        );

      default:
        return (
          <div className="h-full">
            <ClientView 
              project={currentProject} 
              tasks={currentTasks}
              onTaskUpdate={(taskId, status) => {
                console.log('Task updated:', taskId, status);
              }}
            />
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Sidebar Navigation */}
      <div className="w-20 bg-white border-r border-gray-200 flex flex-col items-center py-6 space-y-2 fixed left-0 top-0 h-full z-40">
        {/* Logo */}
        <div className="mb-6">
          <img 
            src="/tyler-technologies-logo.png" 
            alt="Tyler Technologies Logo" 
            className="w-12 h-12 object-contain drop-shadow-sm"
            onError={(e) => console.error('Tyler Technologies logo failed to load:', e)}
          />
        </div>
        
        {/* Navigation Icons */}
        {[
          { key: 'overview', label: 'Overview', icon: Target },
          { key: 'work', label: 'Work', icon: CheckCircle },
          { key: 'timeline', label: 'Timeline', icon: Calendar },
          { key: 'charts', label: 'Charts', icon: BarChart3 },
          { key: 'resources', label: 'Resources', icon: Users },
          { key: 'timetracking', label: 'Time Tracking', icon: Clock },
          { key: 'billing', label: 'Billing Management', icon: DollarSign },
          { key: 'files', label: 'Files', icon: FileText },
          { key: 'notifications', label: 'Notifications', icon: Bell },
          { key: 'messages', label: 'Messages', icon: MessageSquare },
          { key: 'knowledge', label: 'Learning & Resources', icon: BookOpen },
          ...(new URLSearchParams(window.location.search).get('demo') === '1' ? [{ key: 'demo', label: 'Demo', icon: Activity }] : [])
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setViewMode(key as any)}
            className={`group relative flex items-center justify-center w-14 h-14 rounded-lg transition-all duration-300 ${
              viewMode === key 
                ? 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white shadow-lg' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
            title={label}
          >
            <Icon className="w-6 h-6 flex-shrink-0" />
            
            {/* Tooltip */}
            <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              {label}
            </div>
          </button>
        ))}
      </div>

      {/* Top Right Icons */}
      <div className="fixed top-4 right-4 flex items-center gap-3 z-50">
        <button 
          onClick={() => {
            const currentUrl = window.location.href;
            const detachedUrl = currentUrl.includes('?') 
              ? `${currentUrl}&detached=1` 
              : `${currentUrl}?detached=1`;
            
            const newWindow = window.open(
              detachedUrl,
              'onboarding-hub',
              'width=1200,height=800,scrollbars=yes,resizable=yes'
            );

            if (newWindow) {
              newWindow.addEventListener('beforeunload', () => {
                localStorage.setItem('onboarding-hub-window', JSON.stringify({
                  width: newWindow.outerWidth,
                  height: newWindow.outerHeight,
                  x: newWindow.screenX,
                  y: newWindow.screenY
                }));
              });
            }
          }}
          className="p-2 tyler-card hover:bg-white rounded-lg transition-colors"
          title="Pop out to new window"
        >
          <ExternalLink className="w-5 h-5 text-gray-600" />
        </button>
        
        <button 
          onClick={() => setNotificationsEnabled(!notificationsEnabled)}
          className={`p-2 tyler-card hover:bg-white rounded-lg transition-colors ${
            notificationsEnabled ? 'bg-blue-50 border-blue-200' : 'bg-gray-50'
          }`}
          title={notificationsEnabled ? "Notifications enabled - click to disable" : "Notifications disabled - click to enable"}
        >
          <Bell className={`w-5 h-5 transition-colors ${
            notificationsEnabled ? 'text-blue-600' : 'text-gray-400'
          }`} />
          {notificationsEnabled && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white"></div>
          )}
        </button>
        
        <button 
          onClick={() => setShowSettings(true)}
          className="p-2 tyler-card hover:bg-white rounded-lg transition-colors"
        >
          <Settings className="w-5 h-5 text-gray-600" />
        </button>
      </div>
      
      {/* Main Content Area */}
      <div className="flex-1 ml-20 p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-2">
            <h1 className="text-3xl font-bold">
              <span className="bg-gradient-to-r from-[#4267A1] via-[#5A7BC4] to-[#4267A1] bg-clip-text text-transparent drop-shadow-sm">
                Success Bridge
              </span>
            </h1>

            <p className="text-slate-400 mt-1">
              Agentic project onboarding and delivery management
            </p>
          </div>
          
        </div>

        {/* Main Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={viewMode}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Settings Panel */}
      <LaunchPadSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        projectId={projects[0]?.id}
        userRole="admin"
      />
    </div>
  );
};

export default OnboardingHub;
